import { Box, Stack } from '@mui/material';
import { useCallback } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { TextInput } from '@/components/form/baseInputs/TextInput';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useForm } from '@/components/react-hook-form';

import { useUpdateTenantFromAdminMutation } from '../graphql/AdminConfigurationsPage.generated';

interface WorkOSClientIDsSectionProps {
  tenantId: string;
  existingWorkosOauthClientID: string | null;
  existingWorkosM2mClientID: string | null;
  isTenantDetailsLoaded: boolean;
}

interface WorkOSClientIDsFormShape {
  workosOauthClientID: string;
  workosM2mClientID: string;
}

export function WorkOSClientIDsSection({
  tenantId,
  existingWorkosOauthClientID,
  existingWorkosM2mClientID,
  isTenantDetailsLoaded,
}: WorkOSClientIDsSectionProps) {
  const { showFeedback, createErrorFeedback } = useFeedback();

  const formMethods = useForm<WorkOSClientIDsFormShape>({
    defaultValues: {
      workosOauthClientID: existingWorkosOauthClientID ?? '',
      workosM2mClientID: existingWorkosM2mClientID ?? '',
    },
  });

  const [updateTenant, { loading }] = useUpdateTenantFromAdminMutation();

  const oauthValue = formMethods.watch('workosOauthClientID') ?? '';
  const m2mValue = formMethods.watch('workosM2mClientID') ?? '';
  const trimmedOauthValue = oauthValue.trim();
  const trimmedM2mValue = m2mValue.trim();

  const canSaveOauth = Boolean(tenantId && trimmedOauthValue);
  const canSaveM2m = Boolean(tenantId && trimmedM2mValue);
  const canClearOauth =
    Boolean(tenantId) &&
    isTenantDetailsLoaded &&
    Boolean(existingWorkosOauthClientID);
  const canClearM2m =
    Boolean(tenantId) &&
    isTenantDetailsLoaded &&
    Boolean(existingWorkosM2mClientID);

  const onSaveOauth = useCallback(() => {
    if (!canSaveOauth) {
      return;
    }

    void updateTenant({
      variables: {
        input: {
          id: tenantId,
          update: {
            workosOauthClientID: trimmedOauthValue,
          },
        },
      },
      onCompleted: () => {
        // Rebase the field's default to the saved value so the form is no
        // longer dirty; otherwise useForm keeps blocking navigation with a
        // spurious "leave page?" prompt after a successful save.
        formMethods.resetField('workosOauthClientID', {
          defaultValue: trimmedOauthValue,
        });
        showFeedback('WorkOS OAuth client ID updated successfully', {
          variant: 'success',
        });
      },
      onError: createErrorFeedback('Failed to update WorkOS OAuth client ID'),
    });
  }, [
    canSaveOauth,
    createErrorFeedback,
    formMethods,
    showFeedback,
    tenantId,
    trimmedOauthValue,
    updateTenant,
  ]);

  const onSaveM2m = useCallback(() => {
    if (!canSaveM2m) {
      return;
    }

    void updateTenant({
      variables: {
        input: {
          id: tenantId,
          update: {
            workosM2mClientID: trimmedM2mValue,
          },
        },
      },
      onCompleted: () => {
        // See onSaveOauth: rebase the default to clear the dirty state and
        // avoid a false navigation-block prompt after saving.
        formMethods.resetField('workosM2mClientID', {
          defaultValue: trimmedM2mValue,
        });
        showFeedback('WorkOS M2M client ID updated successfully', {
          variant: 'success',
        });
      },
      onError: createErrorFeedback('Failed to update WorkOS M2M client ID'),
    });
  }, [
    canSaveM2m,
    createErrorFeedback,
    formMethods,
    showFeedback,
    tenantId,
    trimmedM2mValue,
    updateTenant,
  ]);

  const onClearOauth = useCallback(() => {
    if (!canClearOauth) {
      return;
    }

    void updateTenant({
      variables: {
        input: {
          id: tenantId,
          update: {
            clearWorkosOauthClientID: true,
          },
        },
      },
      onCompleted: () => {
        // resetField (not setValue) so the cleared field is no longer dirty,
        // matching the save handlers and avoiding a false navigation prompt.
        formMethods.resetField('workosOauthClientID', { defaultValue: '' });
        showFeedback('WorkOS OAuth client ID cleared successfully', {
          variant: 'success',
        });
      },
      onError: createErrorFeedback('Failed to clear WorkOS OAuth client ID'),
    });
  }, [
    canClearOauth,
    createErrorFeedback,
    formMethods,
    showFeedback,
    tenantId,
    updateTenant,
  ]);

  const onClearM2m = useCallback(() => {
    if (!canClearM2m) {
      return;
    }

    void updateTenant({
      variables: {
        input: {
          id: tenantId,
          update: {
            clearWorkosM2mClientID: true,
          },
        },
      },
      onCompleted: () => {
        // See onClearOauth: resetField clears the dirty state to avoid a
        // false navigation prompt.
        formMethods.resetField('workosM2mClientID', { defaultValue: '' });
        showFeedback('WorkOS M2M client ID cleared successfully', {
          variant: 'success',
        });
      },
      onError: createErrorFeedback('Failed to clear WorkOS M2M client ID'),
    });
  }, [
    canClearM2m,
    createErrorFeedback,
    formMethods,
    showFeedback,
    tenantId,
    updateTenant,
  ]);

  return (
    <Box component="form" onSubmit={(e) => e.preventDefault()}>
      <Stack spacing={3} maxWidth={400}>
        <Stack spacing={1}>
          <TextInput
            label="WorkOS OAuth client ID"
            placeholder="client_YYY"
            helpText="WorkOS Connect (OAuth) application client_id. Connect access tokens must carry an app_client_id claim matching this value."
            {...formMethods.register('workosOauthClientID')}
          />
          <Stack direction="row" spacing={1}>
            <Button
              loading={loading}
              variant="primary"
              type="button"
              size="sm"
              disabled={!canSaveOauth}
              onClick={onSaveOauth}
            >
              Save OAuth client ID
            </Button>
            <Button
              variant="destructive-secondary"
              type="button"
              size="sm"
              disabled={!canClearOauth || loading}
              onClick={onClearOauth}
            >
              Clear OAuth client ID
            </Button>
          </Stack>
        </Stack>
        <Stack spacing={1}>
          <TextInput
            label="WorkOS M2M client ID"
            placeholder="client_YYY"
            helpText="WorkOS M2M application client_id. M2M access tokens must carry an app_client_id claim matching this value."
            {...formMethods.register('workosM2mClientID')}
          />
          <Stack direction="row" spacing={1}>
            <Button
              loading={loading}
              variant="primary"
              type="button"
              size="sm"
              disabled={!canSaveM2m}
              onClick={onSaveM2m}
            >
              Save M2M client ID
            </Button>
            <Button
              variant="destructive-secondary"
              type="button"
              size="sm"
              disabled={!canClearM2m || loading}
              onClick={onClearM2m}
            >
              Clear M2M client ID
            </Button>
          </Stack>
        </Stack>
        {!isTenantDetailsLoaded && (
          <Callout severity="warning">
            Clear is unavailable until tenant details load successfully.
          </Callout>
        )}
      </Stack>
    </Box>
  );
}
